import React, { useState } from "react";
import { Button, Badge, Progress, Avatar } from "flowbite-react";
import { HiOutlineCheckCircle } from "react-icons/hi";
import { useNavigate } from "react-router-dom";
import ShowSingleProjectModal from "../../../pages/RoadMapNew/ShowSingleProjectModal";
import { 
  getStatusIcon, 
  getStatusColor, 
  getProgressColor,
  formatStatusForDisplay
} from "./StatusUtils";
import { 
  getSourceFromObject, 
  getFeatureTitle, 
  getFeatureEntityName,
  getTaskEntityName,
  getCompletionPercentage,
  Feature
} from "./SourceUtils";

interface FeatureDetailCardProps {
  feature: Feature;
  openTasks?: () => void;
  currentProject?: any;
  projectName: string;
  epicName: string;
}

const FeatureDetailCard: React.FC<FeatureDetailCardProps> = ({ 
  feature, 
  openTasks,
  currentProject = null,
  projectName,
  epicName
}) => {
  const navigate = useNavigate();
  const [showProjectModal, setShowProjectModal] = useState(false);
  const projectId = currentProject && feature ? feature.id : "5093f5aa-2ced-4c61-9980-e22746dda4ef"; // Use feature ID if available
  
  const handleOpenTasks = () => {
    if (openTasks) {
      // Instead of navigating, open the drawer
      setShowProjectModal(true);
    }
  };

  // Get source from feature
  const source = getSourceFromObject(feature);
  
  // Get the task entity name
  const taskEntity = getTaskEntityName(source);

  // Get completion percentage for API projects
  const getProjectCompletion = () => {
    if (currentProject && typeof currentProject.progress === 'number') {
      // Use the progress value directly if available
      return Math.round(currentProject.progress);
    } else if (currentProject && currentProject.stats) {
      // Calculate from stats if progress not available
      const stats = currentProject.stats;
      const total = stats.total || 0;
      const completed = stats.completed || 0;
      
      if (total > 0) {
        return Math.round((completed / total) * 100);
      }
    }
    return 0;
  };

  // Get task counters for API projects
  const getTaskStats = () => {
    if (currentProject && currentProject.stats) {
      return {
        completed: currentProject.stats.completed || 0,
        total: currentProject.stats.total || 0,
        remaining: (currentProject.stats.total || 0) - (currentProject.stats.completed || 0)
      };
    }
    
    return {
      completed: feature.tasksCompleted || 0,
      total: feature.totalTasks || 0,
      remaining: (feature.totalTasks || 0) - (feature.tasksCompleted || 0)
    };
  };

  const taskStats = getTaskStats();
  const completion = currentProject 
    ? getProjectCompletion()
    : (feature.completion || getCompletionPercentage(feature.tasksCompleted || 0, feature.totalTasks || 0));

  return (
    <>
      <div className="min-w-[400px] max-w-[400px] bg-white dark:bg-slate-900 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700 overflow-hidden flex flex-col" style={{ maxHeight: "600px" }}>
        <div className="p-4 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/80 flex justify-between items-center">
          <h3 className="font-semibold text-slate-900 dark:text-white">
            {getFeatureTitle(source)}
          </h3>
        </div>
        <div className="p-4 flex-1 overflow-y-auto">
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Badge
                color="info"
                className={getStatusColor(feature.status)}
              >
                <div className="flex items-center gap-1">
                  {getStatusIcon(feature.status)}
                  <span>{formatStatusForDisplay(feature.status)}</span>
                </div>
              </Badge>
              <span className="text-sm text-slate-500 dark:text-gray-300">
                {feature.startDate} - {feature.endDate}
              </span>
            </div>

            <h4 className="font-medium text-lg text-slate-900 dark:text-white">
              {getFeatureEntityName(source)}: {feature.name}
            </h4>

            <div className="bg-slate-50 dark:bg-[#2f3a4c] p-4 rounded-lg">
              <h4 className="font-medium mb-3 text-slate-900 dark:text-white">
                {getFeatureEntityName(source)} Details
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-xs text-slate-500 dark:text-gray-300">
                    People Allocated
                  </div>
                  <div className="text-lg font-semibold text-slate-900 dark:text-white">
                    {currentProject ? currentProject.people_allocated : feature.peopleAllocated}
                  </div>
                </div>
                
                <div>
                  <div className="text-xs text-slate-500 dark:text-gray-300">
                    Progress
                  </div>
                  <div className="text-lg font-semibold text-slate-900 dark:text-white">
                    {completion}%
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-slate-50 dark:bg-[#2f3a4c] p-4 rounded-lg">
              <h4 className="font-medium mb-2 text-slate-900 dark:text-white">{taskEntity} Progress</h4>
              <div className="space-y-3 mt-2">
                <div className="space-y-1">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-500 dark:text-gray-300">
                      Completion
                    </span>
                    <span className="font-medium text-slate-900 dark:text-white">
                      {completion}%
                    </span>
                  </div>
                  <div className="h-2 rounded-full bg-slate-200 dark:bg-slate-700">
                    <div 
                      className={`h-full rounded-full ${getProgressColor(completion)}`}
                      style={{ width: `${completion}%` }}
                    ></div>
                  </div>
                </div>

                <div className="flex items-center gap-2 mt-2">
                  <div className="h-2 bg-blue-500 rounded-full w-3" />
                  <div className="text-xs text-slate-500 dark:text-gray-300">
                    Completed
                  </div>
                  <div className="font-medium text-xs text-slate-900 dark:text-white">
                    {taskStats.completed}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="h-2 bg-slate-300 dark:bg-slate-600 rounded-full w-3" />
                  <div className="text-xs text-slate-500 dark:text-gray-300">
                    Remaining
                  </div>
                  <div className="font-medium text-xs text-slate-900 dark:text-white">
                    {taskStats.remaining}
                  </div>
                </div>
              </div>
            </div>

            {currentProject && currentProject.stats && (
              <div className="bg-slate-50 dark:bg-[#2f3a4c] p-4 rounded-lg">
                <h4 className="font-medium mb-3 text-slate-900 dark:text-white">{taskEntity}s Status</h4>
                
                <div className="grid grid-cols-4 gap-2">
                  {/* Row 1 - All 8 status boxes in a single responsive grid */}
                  <div className="bg-white dark:bg-slate-700 p-2 rounded-lg shadow-sm flex flex-col items-center justify-center h-[70px]">
                    <div className="text-xs text-slate-500 dark:text-gray-400 text-center mb-1 truncate w-full px-1" title={`Total ${taskEntity}s`}>Total {taskEntity}s</div>
                    <div className="text-lg font-semibold text-slate-900 dark:text-white">
                      {currentProject.stats.total || 0}
                    </div>
                  </div>
                  
                  <div className="bg-white dark:bg-slate-700 p-2 rounded-lg shadow-sm flex flex-col items-center justify-center h-[70px]">
                    <div className="text-xs text-slate-500 dark:text-gray-400 text-center mb-1 truncate w-full px-1" title="Completed">Completed</div>
                    <div className="text-lg font-semibold text-emerald-600 dark:text-emerald-400">
                      {currentProject.stats.completed || 0}
                    </div>
                  </div>
                  
                  <div className="bg-white dark:bg-slate-700 p-2 rounded-lg shadow-sm flex flex-col items-center justify-center h-[70px]">
                    <div className="text-xs text-slate-500 dark:text-gray-400 text-center mb-1 truncate w-full px-1" title="Running">Running</div>
                    <div className="text-lg font-semibold text-blue-600 dark:text-blue-400">
                      {currentProject.stats.running || 0}
                    </div>
                  </div>
                  
                  <div className="bg-white dark:bg-slate-700 p-2 rounded-lg shadow-sm flex flex-col items-center justify-center h-[70px]">
                    <div className="text-xs text-slate-500 dark:text-gray-400 text-center mb-1 truncate w-full px-1" title="On Track">On Track</div>
                    <div className="text-lg font-semibold text-green-600 dark:text-green-400">
                      {currentProject.stats.on_track || 0}
                    </div>
                  </div>
                  
                  <div className="bg-white dark:bg-slate-700 p-2 rounded-lg shadow-sm flex flex-col items-center justify-center h-[70px]">
                    <div className="text-xs text-slate-500 dark:text-gray-400 text-center mb-1 truncate w-full px-1" title="Delayed">Delayed</div>
                    <div className="text-lg font-semibold text-amber-600 dark:text-amber-400">
                      {currentProject.stats.delayed || 0}
                    </div>
                  </div>
                  
                  <div className="bg-white dark:bg-slate-700 p-2 rounded-lg shadow-sm flex flex-col items-center justify-center h-[70px]">
                    <div className="text-xs text-slate-500 dark:text-gray-400 text-center mb-1 truncate w-full px-1" title="Critical Delays">Critical</div>
                    <div className="text-lg font-semibold text-rose-600 dark:text-rose-400">
                      {currentProject.stats.delayed_critical || 0}
                    </div>
                  </div>
                  
                  <div className="bg-white dark:bg-slate-700 p-2 rounded-lg shadow-sm flex flex-col items-center justify-center h-[70px]">
                    <div className="text-xs text-slate-500 dark:text-gray-400 text-center mb-1 truncate w-full px-1" title="Unallocated">Unallocated</div>
                    <div className="text-lg font-semibold text-slate-600 dark:text-slate-300">
                      {currentProject.stats.unallocated || 0}
                    </div>
                  </div>
                  
                  <div className="bg-white dark:bg-slate-700 p-2 rounded-lg shadow-sm flex flex-col items-center justify-center h-[70px]">
                    <div className="text-xs text-slate-500 dark:text-gray-400 text-center mb-1 truncate w-full px-1" title="Waiting">Waiting</div>
                    <div className="text-lg font-semibold text-purple-600 dark:text-purple-400">
                      {currentProject.stats.waiting || 0}
                    </div>
                  </div>
                </div>

                {/* Add visual task distribution bar */}
                {currentProject.stats.total > 0 && (
                  <div className="mt-4">
                    <div className="text-xs text-slate-500 dark:text-gray-400 mb-2">{taskEntity} Distribution</div>
                    <div className="flex h-5 rounded-md overflow-hidden">
                      {currentProject.stats.completed > 0 && (
                        <div 
                          className="bg-emerald-500" 
                          style={{ width: `${(currentProject.stats.completed / currentProject.stats.total) * 100}%` }}
                          title={`Completed: ${currentProject.stats.completed}`}
                        ></div>
                      )}
                      {currentProject.stats.on_track > 0 && (
                        <div 
                          className="bg-green-500" 
                          style={{ width: `${(currentProject.stats.on_track / currentProject.stats.total) * 100}%` }}
                          title={`On Track: ${currentProject.stats.on_track}`}
                        ></div>
                      )}
                      {currentProject.stats.running > 0 && (
                        <div 
                          className="bg-blue-500" 
                          style={{ width: `${(currentProject.stats.running / currentProject.stats.total) * 100}%` }}
                          title={`Running: ${currentProject.stats.running}`}
                        ></div>
                      )}
                      {currentProject.stats.waiting > 0 && (
                        <div 
                          className="bg-purple-500" 
                          style={{ width: `${(currentProject.stats.waiting / currentProject.stats.total) * 100}%` }}
                          title={`Waiting: ${currentProject.stats.waiting}`}
                        ></div>
                      )}
                      {currentProject.stats.delayed > 0 && (
                        <div 
                          className="bg-amber-500" 
                          style={{ width: `${(currentProject.stats.delayed / currentProject.stats.total) * 100}%` }}
                          title={`Delayed: ${currentProject.stats.delayed}`}
                        ></div>
                      )}
                      {currentProject.stats.delayed_critical > 0 && (
                        <div 
                          className="bg-rose-500" 
                          style={{ width: `${(currentProject.stats.delayed_critical / currentProject.stats.total) * 100}%` }}
                          title={`Critical Delayed: ${currentProject.stats.delayed_critical}`}
                        ></div>
                      )}
                      {currentProject.stats.unallocated > 0 && (
                        <div 
                          className="bg-slate-400" 
                          style={{ width: `${(currentProject.stats.unallocated / currentProject.stats.total) * 100}%` }}
                          title={`Unallocated: ${currentProject.stats.unallocated}`}
                        ></div>
                      )}
                    </div>
                    
                    {/* Legend */}
                    <div className="flex flex-wrap gap-2 mt-3">
                      {currentProject.stats.completed > 0 && (
                        <div className="flex items-center gap-1">
                          <div className="w-3 h-3 bg-emerald-500 rounded-sm"></div>
                          <span className="text-xs text-slate-500 dark:text-gray-400">Completed</span>
                        </div>
                      )}
                      {currentProject.stats.on_track > 0 && (
                        <div className="flex items-center gap-1">
                          <div className="w-3 h-3 bg-green-500 rounded-sm"></div>
                          <span className="text-xs text-slate-500 dark:text-gray-400">On Track</span>
                        </div>
                      )}
                      {currentProject.stats.running > 0 && (
                        <div className="flex items-center gap-1">
                          <div className="w-3 h-3 bg-blue-500 rounded-sm"></div>
                          <span className="text-xs text-slate-500 dark:text-gray-400">Running</span>
                        </div>
                      )}
                      {currentProject.stats.waiting > 0 && (
                        <div className="flex items-center gap-1">
                          <div className="w-3 h-3 bg-purple-500 rounded-sm"></div>
                          <span className="text-xs text-slate-500 dark:text-gray-400">Waiting</span>
                        </div>
                      )}
                      {currentProject.stats.delayed > 0 && (
                        <div className="flex items-center gap-1">
                          <div className="w-3 h-3 bg-amber-500 rounded-sm"></div>
                          <span className="text-xs text-slate-500 dark:text-gray-400">Delayed</span>
                        </div>
                      )}
                      {currentProject.stats.delayed_critical > 0 && (
                        <div className="flex items-center gap-1">
                          <div className="w-3 h-3 bg-rose-500 rounded-sm"></div>
                          <span className="text-xs text-slate-500 dark:text-gray-400">Critical</span>
                        </div>
                      )}
                      {currentProject.stats.unallocated > 0 && (
                        <div className="flex items-center gap-1">
                          <div className="w-3 h-3 bg-slate-400 rounded-sm"></div>
                          <span className="text-xs text-slate-500 dark:text-gray-400">Unallocated</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {!currentProject && (
              <div className="bg-slate-50 dark:bg-[#2f3a4c] p-4 rounded-lg">
                <h4 className="font-medium mb-3 text-slate-900 dark:text-white">{getFeatureEntityName(source)} Details</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-xs text-slate-500 dark:text-gray-300">
                      Blockers
                    </div>
                    <div className="text-lg font-semibold text-slate-900 dark:text-white">
                      {feature.blockers}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-slate-500 dark:text-gray-300">
                      {taskEntity}s Completed
                    </div>
                    <div className="text-lg font-semibold text-slate-900 dark:text-white">
                      {feature.tasksCompleted}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-slate-500 dark:text-gray-300">
                      Total {taskEntity}s
                    </div>
                    <div className="text-lg font-semibold text-slate-900 dark:text-white">
                      {feature.totalTasks}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="p-4 border-t border-slate-200 dark:border-slate-700">
          <Button
            className="w-full"
            color="light"
            onClick={handleOpenTasks}
          >
            <HiOutlineCheckCircle className="h-4 w-4 mr-2" />
            View {taskEntity}s
          </Button>
        </div>
      </div>
      
      {/* Project Tasks Drawer */}
      <ShowSingleProjectModal 
        isVisible={showProjectModal}
        setShowModal={setShowProjectModal}
        project_id={projectId}
      />
    </>
  );
};

export default FeatureDetailCard;